import type { SxProps } from '@mui/material';
import { HelpText } from '@ovComponents/2-component/helpText/helpText';
import { Radio, RadioGroup } from '@ovComponents/2-component/radio/radio';
import { Box, Stack, Typography, useTheme } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CompletionBox } from '../completionBox/completionBox';
import { useSelectionTileTokens } from './selectionTile.tokens';

type SelectionTileProps = {
  direction?: 'row' | 'column';
  type?: 'checkbox' | 'radio' | 'tile' | 'completion';
  value?: string | number;
  options: Array<{
    label: string;
    subtitle?: string;
    value: string | number;
    labelSideComponent?: React.ReactNode;
  }>;
  onChange: (event: any) => void;
  error?: boolean;
  errorText?: string;
  component?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  selectedComponent?: 'SUGGESTED' | 'OTHER' | 'DEFAULT';
  infoTooltip?: string;
  testId?: string;
  label?: string;
  disabled?: boolean;
  xs?: number;
  sx?: SxProps;
  innerSx?: SxProps;
};

export const SelectionTile = ({
  direction = 'column',
  type = 'radio',
  value,
  infoTooltip,
  options,
  error,
  errorText,
  component,
  selectedComponent,
  testId,
  label,
  disabled = false,
  xs,
  sx,
  innerSx,
  ...restProps
}: SelectionTileProps) => {
  const theme = useTheme();
  const tokens = useSelectionTileTokens(theme);
  const { t } = useTranslation('components');
  const [selectedIndex, setSelectedIndex] = useState<number>();

  const boxStyle: SxProps = {
    border: `1px solid ${tokens.borderColor}`,
    borderRadius: tokens.borderRadius,
    px: '12px',
    py: '2px',
    cursor: 'pointer',
    transition: 'all 0.2s ease-in-out',
    '&:hover': {
      borderColor: tokens.hoverBorderColor,
      backgroundColor: tokens.hoverBackgroundColor,
    },
  };

  const [changing, setChanging] = useState(false);
  const onChange = async (event: any) => {
    setChanging(true);
    await restProps.onChange(event);
    setChanging(false);
  };

  if (type === 'radio') {
    return (
      <>
        {error && (
          <HelpText
            text={errorText ?? t('fieldErrors.selectionTile')}
            tone='error'
          />
        )}
        <RadioGroup
          testId={testId}
          value={value}
          infoTooltip={infoTooltip}
          label={label}
          onChange={onChange}
          fullWidth
          disabled={disabled || changing}
          sx={sx}
        >
          <Stack
            direction={direction}
            spacing={2}
            useFlexGap
            sx={{
              width: '100%',
              flexGrow: 1,
              flexWrap: direction === 'row' ? 'wrap' : undefined,
            }}
          >
            {(options || []).map((option, index) => (
              <Box
                data-testid={
                  testId
                    ? `${testId}-${kebabCase(option.label)}`
                    : `option-${index}`
                }
                key={index}
                sx={{
                  flex:
                    direction === 'row'
                      ? xs
                        ? `1 1 ${(xs / 12) * 100}%`
                        : '1 1 0'
                      : undefined,
                  width: direction === 'column' ? '100%' : undefined,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={boxStyle}
                  onClick={() => onChange({ target: { value: option.value } })}
                >
                  <Radio
                    testId={
                      testId
                        ? `${testId}-${kebabCase(option.label)}-radio`
                        : `radio-${index}`
                    }
                    disabled={disabled}
                    value={option.value}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        {option.label}
                        {option.labelSideComponent}
                      </Box>
                    }
                    size='small'
                  />
                  {option.subtitle && (
                    <Typography
                      component='p'
                      data-testid='subtitle'
                      variant='bodySmall'
                      color='textSecondary'
                      sx={{
                        ml: '28px',
                        mb: '6px',
                        mt: -1,
                        pb: 1,
                      }}
                    >
                      {option.subtitle}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Stack>
        </RadioGroup>
      </>
    );
  }

  if (type === 'completion') {
    return (
      <>
        {(options || []).map((option, index) => (
          <CompletionBox
            testId={testId}
            disabled={disabled || changing}
            title={option.label}
            titleSideComponent={option.labelSideComponent}
            state='next'
            variant='edit'
            type={type}
            onAdd={async () => {
              setSelectedIndex(index);
              await onChange({ target: { value: option.value } });
            }}
            key={option.value}
            selected={
              selectedComponent === component && selectedIndex === index
            }
            sx={sx}
            innerSx={innerSx}
          >
            <Typography
              variant='bodyLarge'
              data-testid={
                testId
                  ? `${testId}-subtitle-${option.label}`
                  : `completion-box-subtitle${option.subtitle}`
              }
              sx={{ color: theme.color.onSurfaceVariant }}
            >
              {option.subtitle}
            </Typography>
          </CompletionBox>
        ))}
      </>
    );
  }

  return (
    // TODO: Checkbox
    <></>
  );
};

export default SelectionTile;
